/* eslint-disable react/require-default-props */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';

import '../react/modelConfiguration/modelConfigurationTree.scss';
import '../react/modelVariants/modelVariants.scss';
import './configurationWidget.scss';

import { Vector3 } from '@babylonjs/core';
import { URLPaths } from '../react/utils/URLPaths';
import { DefaultModelIlluminationData } from './defaultModelIlluminationData';
import { BabylonComponentSceneManager } from './BabylonSceneManager';
import { ConfiguratorWindow } from './configuratorWindow';

import MindLogo from './Resources/mind_logo.jpg';
import LoadingSymbol from './Resources/loading.svg';
import MinimizeSymbol from './Resources/settings.svg';

import { TreeOptionSelections, URLDecodeTreeSelections } from '../modelConfiguration/data/optionsSelection';
import { SLTXFile } from '../fileFormats/sltx/sltxFile';
import { ModelRuleData } from '../modelConfiguration/rules/modelRuleData';
import { ModelIlluminationData } from '../modelIllumination/modelIlluminationData';
import { FilePaths } from '../fileSystem/filePaths';
import { SLTXUtil } from '../fileFormats/sltx/sltxUtil';
import { POSCatalogModelData } from './Data/POSCatalogData';
import { CatalogTagsTranslation } from './Data/catalogTagsTranslation';
import { CameraViewWidget } from './Commands/CameraViewWidget';
import { CustomAttributeValues } from '../customAttributes/customAttributeValue';
import { PriceData } from '../fileFormats/catalog/catalog';
import { ThreeComponentSceneManager } from './ThreeSceneManager';

interface ConfigurationWidgetProps {
  customAttributesValues: CustomAttributeValues,
  modelData: POSCatalogModelData;
  sltxFile: SLTXFile;
  priceData: PriceData[] | undefined,
  configuration: string;
  configurationApplyId: number,
  catalogPath: string;
  resourcesPath: string;
  tagsTranslation: CatalogTagsTranslation[];
  languageSelected: string;
  screenshotId: string | undefined;
  setScreenshotHandler: (fileId: string, fileData: string) => void;
  changeComponentHandler: (selections: TreeOptionSelections) => void;
  changeCustomAttributesHandler: (attributes: CustomAttributeValues) => void;
  enableEdit: boolean;
  onCommit? : (commit: boolean) => void,
  canCommit?: boolean,
}

export function ConfigurationWidgetComponent({
  customAttributesValues,
  modelData,
  sltxFile,
  priceData,
  configuration,
  configurationApplyId,
  catalogPath,
  resourcesPath,
  tagsTranslation,
  languageSelected,
  screenshotId,
  setScreenshotHandler,
  changeComponentHandler,
  changeCustomAttributesHandler,
  enableEdit,
  onCommit = undefined,
  canCommit = false,
}: ConfigurationWidgetProps): JSX.Element {

  // -----------------------------------------------------------------------
  // Select default viewer library (Babylon or Three) based on URL param,
  // default to Babylon if not specified or if invalid value is provided
  // -----------------------------------------------------------------------
  const viewerLibraryParam = new URLSearchParams(window.location.search).get('viewer');
  const bUseThreeViewer = viewerLibraryParam === 'three';

  const [skyBoxPath, setSkyBoxPath] = useState<string>('');
  const [currentConfiguratorPage, setCurrentConfiguratorPage] = useState<number>(0);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoadingPart, setIsLoadingPart] = useState<boolean>(false);
  const [isConfiguratorMinimized, setIsConfiguratorMinimized] = useState<boolean>(false);

  // ---------------------------------------------------------------------------
  //  This is the latest configuration requested from above (parent) component.
  //  It is updated immediately when a new configuration arrives from above.
  //  It is also updated when the user changes the configuration in the widget.
  // ----------------------------------------------------------------------------
  const [requestedVariantComposition, setRequestedVariantComposition] = useState<TreeOptionSelections | undefined>(undefined);

  // ------------------------------------------------------------------
  // This is the configuration actually sent down to Babylon/Three.
  // It is only updated when the viewer is not busy loading.
  // ------------------------------------------------------------------
  const [viewerVariantComposition, setViewerVariantComposition] = useState<TreeOptionSelections | undefined>(undefined);

  const [ready, setReady] = useState<boolean>(false);
  const [modelRules, setModelRules] = useState<ModelRuleData[]>([]);
  const [modelIllumination, setModelIllumination] = useState<ModelIlluminationData>(DefaultModelIlluminationData.get());
  const [cameraPosition, setCameraPosition] = useState<Vector3>(new Vector3(0, 0, 1));

  // -----------------------------------------------------------------------------------
  // Configure Widget branding visibility based on CSS variable --show-label
  // (if set to 'white', branding will be hidden)
  // -----------------------------------------------------------------------------------
  const [showBranding, setShowBranding] = useState(true);

  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    const brandingValue = computedStyle.getPropertyValue('--show-label').trim();
    setShowBranding(brandingValue !== 'white');
  }, []);

  const checkAndSetCurrentConfiguratorPage = (newPage: number) => {
    const variants = modelData.getVariants();
    let pageToBe = newPage;
    if (variants.length === 0
      || (variants.length === 1 && variants[0].name === SLTXUtil.DummyOption)
    ) {
      pageToBe = newPage === 0 ? 1 : newPage;
    }
    setCurrentConfiguratorPage(pageToBe);
  };

  useEffect(() => {
    checkAndSetCurrentConfiguratorPage(0);
  }, []);

  const getPathToResource = (modelResource: string): string => {
    const pathParts = modelResource.split(':');

    let path = modelResource;
    let type = 'sltx';
    if (pathParts.length > 1) [type] = pathParts;

    if (type === 'sltx') {
      path = FilePaths.join(
        FilePaths.join(
          catalogPath,
          modelData.getResourcesPath(),
        ),
        modelResource,
      );
    } else {
      path = FilePaths.join(
        catalogPath,
        pathParts[1],
      );
    }
    return URLPaths.adjustPathToWeb(path);
  };

  useEffect(() => {
    setSkyBoxPath(`${resourcesPath}_Skyboxes_/environment.env`);
  }, []);

  // -------------------------------------------------------------------------------------------
  // Decode/receive configuration from parent, but do NOT immediately push it to Babylon/Three.
  // -------------------------------------------------------------------------------------------
  useEffect(() => {
    let result : TreeOptionSelections | undefined;
    if (configuration && configuration !== '') {
      result = URLDecodeTreeSelections(configuration);
    } else if (sltxFile) {
      result = SLTXUtil.getDefaultComposition(sltxFile.getRootFileContents());
    }

    if (!result) return;

    setRequestedVariantComposition(result);
    changeComponentHandler(result);
    // -----------------------------------------------------------------
    // First ever viewer composition can be initialized immediately.
    // After that, updates are gated by isLoaded in the effect below.
    // -----------------------------------------------------------------
    setViewerVariantComposition((current) => current ?? result);
  }, [configuration, configurationApplyId, sltxFile]);

  // -------------------------------------------------------------------------------
  // Gate the actual viewer composition.
  // If a new configuration arrives while Babylon/Three is loading, it waits here.
  // -------------------------------------------------------------------------------
  useEffect(() => {
    if (!requestedVariantComposition) return;
    if (!viewerVariantComposition) {
      setViewerVariantComposition(requestedVariantComposition);
      return;
    }

    if (!isLoaded || isLoadingPart) {
      return;
    }

    setViewerVariantComposition(requestedVariantComposition);
  }, [requestedVariantComposition, isLoaded, isLoadingPart]);

  useEffect(() => {
    if (sltxFile) setReady(true);

    if (modelData && modelData.catalogData && sltxFile?.rules) {
      setModelRules(sltxFile.rules);
    } else if (modelData && modelData.catalogData) {
      setModelRules(modelData.catalogData.rules);
    }
  }, [sltxFile]);

  useEffect(() => {
    if (modelData && modelData.catalogData && modelData.catalogData.illumination) {
      setModelIllumination(modelData.catalogData.illumination);
    }
  }, [sltxFile]);

  const changeComponent = (selections: TreeOptionSelections) => {
    if (!enableEdit) return;
    if (!isLoaded || isLoadingPart) return;

    const nextSelections = [...selections];

    setRequestedVariantComposition(nextSelections);
    setViewerVariantComposition(nextSelections);
    changeComponentHandler(nextSelections);
  };

  const viewe3dstyle : React.CSSProperties = enableEdit ? {
    position: 'absolute',
    minWidth: '375px',
    width: 'calc(50% - 10px)',
    height: 'calc(100% - 20px)',
    margin: '10px 0px 10px 10px',
  } : {
    position: 'absolute',
    minWidth: '375px',
    width: 'calc(100% - 20px)',
    height: 'calc(100% - 20px)',
    margin: '10px 0px 10px 10px',
  };
  return (
    <>
      {!isLoaded
        && (
          <div style={{
            position: 'absolute', width: '100%', height: '100%', backgroundColor: '#c3c3c3', zIndex: 999999,
          }}
          >
            {showBranding && (
              <img className="center-logo" src={MindLogo} alt="Mind S.A Logo" />
            )}
            <img className="loading-logo" src={LoadingSymbol} alt="Loading..." />
          </div>
        )}
      {isLoadingPart
        && (
          <div style={{
            position: 'absolute', width: '100%', height: '100%', backgroundColor: '#CCCCCCCC', zIndex: 99999999,
          }}
          >
            <img className="loading-logo" src={LoadingSymbol} alt="Loading..." />
          </div>
        )}
      <div style={{
        position: 'absolute', width: '100%', height: '100%',
      }}
      >
        {viewerVariantComposition !== undefined
          && (
            <>
              <div
                className="visualization-3d"
                style={viewe3dstyle}
              >
                {sltxFile
                  && modelData.catalogData
                  && modelIllumination
                  && ready
                  && (
                    <>
                      {bUseThreeViewer ? (
                        <ThreeComponentSceneManager
                          sltxFile={sltxFile}
                          composition={viewerVariantComposition}
                          skyboxPath={skyBoxPath}
                          modelIlluminationData={modelIllumination}
                          modelIlluminationEditable={false}
                          registerCallbackGetModelIlluminationData={undefined}
                          screenshotId={screenshotId}
                          cameraPosition={cameraPosition}
                          setScreenshotHandler={setScreenshotHandler}
                          setIsLoaded={setIsLoaded}
                          setIsLoadingPart={setIsLoadingPart}
                        />
                      ) : (
                        <BabylonComponentSceneManager
                          sltxFile={sltxFile}
                          composition={viewerVariantComposition}
                          skyboxPath={skyBoxPath}
                          modelIlluminationData={modelIllumination}
                          modelIlluminationEditable={false}
                          registerCallbackGetModelIlluminationData={undefined}
                          screenshotId={screenshotId}
                          cameraPosition={cameraPosition}
                          setScreenshotHandler={setScreenshotHandler}
                          setIsLoaded={setIsLoaded}
                          setIsLoadingPart={setIsLoadingPart}
                        />
                      )}
                      <CameraViewWidget setCameraViewHandler={setCameraPosition} />
                    </>
                  )}
              </div>
              {enableEdit ? (
                <div className="selection-controls">
                  {modelData.getVariants().length > 0
                    && sltxFile
                    && isLoaded
                    && !isLoadingPart
                    && (isConfiguratorMinimized ? (
                      <button
                        type="button"
                        className="btn btn-light minimize-btn"
                        onClick={() => { setIsConfiguratorMinimized(false); }}
                      >
                        <img src={MinimizeSymbol} alt="Minimize" />
                      </button>
                    ) : (
                      <ConfiguratorWindow
                        sltxFile={sltxFile}
                        priceData={priceData}
                        customAttributesValues={customAttributesValues}
                        modelRules={modelRules}
                        variants={modelData.getVariants()}
                        optionsSelectionState={requestedVariantComposition ?? viewerVariantComposition}
                        tagsTranslation={tagsTranslation}
                        languageSelected={languageSelected.toLowerCase()}
                        currentPage={currentConfiguratorPage}
                        updateOptionsSelectionState={changeComponent}
                        getResourcePath={getPathToResource}
                        minimizeHandler={setIsConfiguratorMinimized}
                        changeConfiguratorPageHandler={checkAndSetCurrentConfiguratorPage}
                        changeCustomAttributesHandler={changeCustomAttributesHandler}
                        onCommit={onCommit}
                        canCommit={canCommit}
                      />
                    ))}
                </div>
              ) : (
                <div />
              )}
            </>
          )}
      </div>
    </>
  );
}
